import { module, Mutations, BankStrategy } from './helper';
export { default as signupHelper, Mutations } from './helper';
import signup, { apiURL } from '@/api/signup';
import {
    ISignupField,
    OpenBankingProcessState,
    IBankStrategy,
    YodleeStrategy,
    IProgressResponse,
    IBankProvider,
    IPdfFileTransfer,
    OpenBankingStrategy,
} from '@/api/signup/interface';
import { logger } from '@/util/logger';
import router from '@/router';
import { audit } from '@/util/audit';
import { delay } from '@/util/helpers';
import { persistStore } from '@/util/store';
import { StaticPagesEnum, SystemPagesEnum } from '@/util/signup';
import { searchToObject } from '@/util/location';
import { validCredentials } from '@/util/messages';
import { clientResolution } from '@/util/client';
import Axios from 'axios';

export default {
    namespaced: true,
    state: {
        bank: undefined,
        strategy: undefined,
        bankProvider: undefined,
        completeUrl: undefined,
        cancelUrl: undefined,
        signup: {
            fetching: true,
            intervalId: -1,
            requestId: '',
            canceled: false,
            sentValidCreds: false,
            progress: {
                code: OpenBankingProcessState.Created,
            },
        },
    },
    getters: {
        bank: (s) => s.bank,
        dataProviderId: (s) => s.bankProvider && s.bankProvider.dataProviderId,
        allowedProviders: (s) => (s.bankProvider && s.bankProvider.allowedProviders) || [],
        bankProvider: (s) => s.bankProvider,
        completeUrl: (s) => s.completeUrl,
        cancelUrl: (s) => s.cancelUrl,
        strategy: (s) => s.strategy,
        strategyType: (s) => (s.strategy && (s.strategy.type as any)) || null,
        requestId: (s) => s.strategy && s.strategy.data && s.strategy.data.requestId,
        signup: (s) => s.signup,
        bankStrategy: (s, getters) => ({
            OpenBanking: getters.strategyType === BankStrategy.OpenBanking && (s.strategy as any),
        }),
    },
    actions: {
        async init({ commit, state, dispatch }) {
            commit(Mutations.SET_FETCHING, false);
            commit(Mutations.SET_INTERVALID, -1);
            commit(Mutations.SET_REQUESTID, '');
            commit(Mutations.SET_CANCELED, false);
            commit(Mutations.SET_PROGRESS, {
                code: OpenBankingProcessState.Created,
            });
        },
        async saveState({ rootGetters, state }, stateName: string): Promise<any> {
            const token = rootGetters.token;
            localStorage.setItem(stateName, token);
            localStorage.setItem(stateName + ':bank', JSON.stringify(state.bank));
        },
        async loadFromState({ dispatch, commit }, stateName: string): Promise<any> {
            const token = localStorage.getItem(stateName);
            if (!token) {
                console.error('state did not match!');
                return false;
            }
            const bankJson = localStorage.getItem(stateName + ':bank');
            const bank = JSON.parse(bankJson || '');
            commit(Mutations.SET_BANK, bank);
            return token;
        },
        async getCompleteUrl({ commit }): Promise<string> {
            const url = await signup.completeUrl();
            commit(Mutations.SET_COMPLETE_URL, url);
            return url;
        },
        async getCancelUrl({ commit }): Promise<string> {
            const url = await signup.cancelUrl();
            commit(Mutations.SET_CANCEL_URL, url);
            return url;
        },
        async getEarlyRedirectUrl({ commit }): Promise<string> {
            const url = await signup.earlyRedirectUrl();
            return url;
        },
        async setDataProviderId({ commit, state }, dataProviderId: number): Promise<any> {
            commit(Mutations.SET_DATA_PROVIDER, {
                ...state.bankProvider,
                dataProviderId,
            });
        },
        getPdfFile({ getters }): Promise<IPdfFileTransfer> {
            const state = (getters.strategy && getters.strategy.data && getters.strategy.data.state) || 'test';
            console.log('getting pdf for state', state);
            return signup.fetchAccountPdf(state);
        },
        setBank({ commit, dispatch }, bank) {
            commit(Mutations.SET_BANK, bank);
            return dispatch('resolveDataProvider', bank && bank.id);
        },
        async resolveDataProvider({ commit, rootGetters }, bankId) {
            if (!bankId) {
                commit(Mutations.SET_DATA_PROVIDER, undefined);
                return;
            }
            const response = await signup.bankProvider(bankId, rootGetters['settings/allowedProviders']);
            commit(Mutations.SET_DATA_PROVIDER, response);
            return response;
        },
        // requestMastercardRedirect: call mastercardStrategy and return response
        async requestMastercardRedirect(
            { commit, rootGetters, dispatch }: { commit: Function; rootGetters: any; dispatch: Function },
            { language, returnUrl }: { language: string; returnUrl?: string }
            ): Promise<any> {
            try {
                // Get referenceGuid from user state
                const referenceGuid = rootGetters['user/referenceGuid'];
                console.log('referenceGuid from user state', referenceGuid);
                if (!referenceGuid) {
                    throw new Error('referenceGuid is not available in user state');
                }
                
                // build query object expected by the client.get call
                const query: any = {
                ClientReference: referenceGuid,
                Language: language,
                };
                
                // Add returnUrl if provided
                if (returnUrl) {
                    query.ReturnUrl = returnUrl;
                }
                
                // the api client uses `get` helper which ultimately calls axios.request and returns response.data
                const resp = await signup.mastercardStrategy(query);
                // optional: commit into state if you want to persist
                commit(Mutations.SET_COMPLETE_URL, resp.flowUrl || '');
                
                // Save state to localStorage similar to saveState action
                await dispatch('saveState', 'mastercard');
                
                // Also save the Mastercard response data
                //localStorage.setItem('mastercard:response', JSON.stringify(resp));
                //localStorage.setItem('mastercard:referenceGuid', referenceGuid);
                //localStorage.setItem('mastercard:language', language);
                
                return resp;
            } catch (err) {
                logger.error('requestMastercardRedirect failed', err);
                throw err;
            }
        },
        async getBankStrategy({ commit, state, rootGetters, dispatch }) {
            commit(Mutations.RESET_SIGNUP);
            commit(Mutations.SET_FETCHING, true);
            commit(Mutations.SET_CANCELED, false);
            try {
                if (!state.bank) {
                    throw new Error('Bank is required!!');
                }
                const bankId = state.bank.id;
                let dataProviderId = state.bankProvider && state.bankProvider.dataProviderId;
                if (dataProviderId === undefined || dataProviderId === null) {
                    const r: IBankProvider = await dispatch('resolveDataProvider', bankId);
                    dataProviderId = r.dataProviderId;
                }
                const returnUrl = await dispatch('getEarlyRedirectUrl');
                var disableNewTab = window == window.parent ? true : false;
                const disableTabConfigValue = rootGetters['settings/disableNewTab'];
                if (disableTabConfigValue === false) {
                    disableNewTab = false;
                }
                const strategy = await signup.bankStrategy({
                    bankId,
                    dataProviderId,
                    returnUrl,
                    disableNewTab
                });
                if (!strategy) {
                    return undefined;
                }
                commit(Mutations.SET_STRATEGY, strategy);
                commit(Mutations.SET_REQUESTID, (strategy.data as any).requestId);
                return strategy;
            } finally {
                commit(Mutations.SET_FETCHING, false);
            }
        },

        async getLookupInfo({ commit, state, rootGetters, dispatch }) {

            try {
                var requestId = state.strategy.data.requestId;
                var clientCode = clientResolution.clientCode;
                var timeOut = rootGetters['settings/reverseLookUpRequestWaitTimeInSec'] * 1000;
                const lookupInfo = signup.reverseLookUpInfoRequest({ requestId, clientCode, timeOut });
                if (!lookupInfo) {
                    return undefined;
                }

                return lookupInfo;
            }
            catch {
                return { code: 500 };
            }
            finally {

            }
        },
        clearBank({ commit, dispatch }) {
            commit(Mutations.SET_STRATEGY, undefined);
            return dispatch('navigation/navigateTo', 'connect', { root: true });
        },
        async cancelCurrentRequest({ commit, dispatch }) {
            commit(Mutations.SET_CANCELED, true);
        },
        async getProgress({ state, commit, dispatch }) {
            try {
                if (state.signup.canceled) {
                    return { code: 700 };
                }
                const p = await signup.requestProgress((state.strategy && state.strategy.data.requestId) || '');
                commit(Mutations.SET_PROGRESS, p);
                logger.log('progress', { ...p });
                return p;
            } catch {
                return { code: 500 };
            }
        },
        async completeSignup({ dispatch, commit, state, rootState, rootGetters }) {
            const disableTab = rootGetters['settings/disableNewTab'];
            const thirdPartyIntegrationApp = rootGetters['settings/thirdPartyIntegrationApp'];
            const earlyRedirectEnabled = rootGetters['settings/earlyRedirectEnabled'];

            var w: Window | null;
            var iframe = window !== window.parent ? true : false;
            if (state.bankProvider && (state.bankProvider.dataProviderId === 0 || state.bankProvider.dataProviderId === 5 || state.bankProvider.dataProviderId === 6)) {
                if (disableTab === true && iframe === false) {
                    w = window;
                }
                else if (thirdPartyIntegrationApp === true && earlyRedirectEnabled === true) {
                    w = window.open(window.location.origin + '/ThirdPartyThankyou.html', '_system');
                }
                else {
                    w = window.open(window.location.origin + '/redirect.html', '_system');
                }
            }
            else {
                w = null;
            }

            var limitExceeds: boolean = false;
            await dispatch('getCompleteUrl');
            logger.log('completesigup -- ');
            const strategy: IBankStrategy | null = await dispatch('getBankStrategy')
                .then(async (x: IBankStrategy) => {
                    if (!x) {
                        logger.critical('error getting strategy');
                        commit(Mutations.SET_CANCELED, true);
                        await dispatch('navigation/goToError', null, { root: true });
                        return null;
                    }
                    if (x.data && x.data.state == "AccountLimitExceeds") {
                        limitExceeds = true;
                        if (w) w.close();
                        commit(Mutations.SET_CANCELED, true);
                        return dispatch('navigation/goToError', { message: "Number of accounts exceeds the limit", allowRetry: false }, { root: true });
                    }
                    switch (x.type) {
                        case 'Yodlee':
                            break;
                        case 'YodleePdf':
                            break;
                        case 'OpenBanking':
                        case 'Starling':
                        case 'TrueLayer':
                            if ((x.data as any).redirectUrl && w) {
                                const obStrategy = x as OpenBankingStrategy;
                                obStrategy.data.disableNewTab = window !== window.parent ? true : false;
                                const fromHtml = `<form id="obForm" method="POST" action="${apiURL + '/api/bank/redirect'}">
                                    ${Object.keys(obStrategy.data).map((k) => `<input type="hidden" name="${k}" value="${obStrategy.data[k]}"/>`).join(' ')}
                                    </form>`;
                                w.document.body.innerHTML += `${fromHtml}`;
                                const script = document.createElement("script")
                                script.innerHTML = `document.getElementById("obForm").submit()`
                                try {
                                    w.document.body.appendChild(script)
                                } catch (error) {
                                    w.document.write(`
                                        ${fromHtml}
                                        <script>
                                            document.getElementById("obForm").submit()
                                        </script>`);
                                }
                                persistStore(x.data.state, rootState);

                            } else {
                                if (w) w.close();
                            }
                            break;
                        default:
                            logger.error('unknown strategy type', x.type);
                            return null;
                    }
                    return x;
                })
                .catch((e) => {
                    logger.critical('error', e);
                    if (w) w.close();
                    return null;
                });
            if (strategy) {
                audit({
                    action: 'found-strategy',
                    info: strategy,
                });
            }
            if (w) {
                if (w.closed) {
                    if (limitExceeds) return; //skipping method for duplicating dispatch to error
                    logger.log('w.close -> closing window');
                    // if the user didn't wait and closed the window
                    commit(Mutations.SET_CANCELED, true);
                    await dispatch('navigation/goToError', null, { root: true });
                } else {
                    // const checkChild = () => {
                    //   if (w && w.closed) {
                    //     logger.warn('Child window closed');
                    //     clearInterval(timerId);
                    //     dispatch('getProgress').then(({ code }) => {
                    //       logger.log('checkChild -> closing window ' + code);
                    //       // if we havent start processing it.
                    //       if (code === OpenBankingProcessState.Created) {
                    //         commit(Mutations.SET_CANCELED, true);
                    //         dispatch(
                    //           'navigation/goToError',
                    //           { title: 'Cancelled' },
                    //           { root: true },
                    //         );
                    //       }
                    //     });
                    //   }
                    // };
                    // const timerId = setInterval(checkChild, 500);
                }
            } else {
                // yodlee
            }
            if (strategy) {
                logger.log('found strategy ', strategy);
                dispatch('navigation/navigateTo', SystemPagesEnum.Strategy, {
                    root: true,
                });
            }
            return strategy;
        },
        async trackRequest({ state, dispatch }) {
            const response: IProgressResponse = await dispatch('getProgress');
            if (!response) {
                return;
            }
            const { code } = response;
            if ((code >= OpenBankingProcessState.Success && code !== OpenBankingProcessState.NotFound) || state.signup.canceled) {
                return;
            }
            if (code >= OpenBankingProcessState.GettingTransactions && !state.signup.sentValidCreds) {
                validCredentials();
                state.signup.sentValidCreds = true;
            }
            delay(5000).then(() => dispatch('trackRequest'));
        },
        validatePage(_, { page, values }) {
            return signup.validatePage(page, values);
        },
        validateCurrentPage({ rootGetters }, { page, values }) {
            const currentPage = rootGetters['navigation/page'];
            logger.debug('validate current page', currentPage, page);
            if (currentPage !== page) {
                logger.error('invalid page detected, expected ', currentPage, 'but instead got', page);
                return false;
            }
            return signup.validatePage(page, values);
        },
        async processYodlee({ commit, dispatch }, request) {
            try {
                commit(Mutations.SET_CANCELED, false);
                const response = await signup.processBankStrategy(request).catch(async (x) => {
                    await dispatch('getProgress');
                    return {
                        success: false,
                        error: x.error,
                        fields: [],
                    };
                });
                if (!response.success) {
                    if (!response.fields || response.fields.length === 0) {
                        return dispatch('navigation/goToError', { message: response.error }, { root: true });
                    }
                    audit({
                        action: 'yodlee-mfa',
                        info: response,
                    });
                    router.push({
                        name: SystemPagesEnum.Strategy,
                        params: {
                            ...request,
                            title: 'Please answer the following',
                            fields: response.fields,
                            isMfa: true,
                        } as any,
                        query: router.currentRoute.query,
                    });
                } else {
                    dispatch('trackRequest');
                }
                return response.success;
            } catch (e) {
                return false;
            }
        },
    },
    mutations: {
        [Mutations.SET_BANK](state, bank) { state.bank = bank; },
        [Mutations.SET_STRATEGY](state, strategy) { state.strategy = strategy; },
        [Mutations.SET_DATA_PROVIDER](state, bankProvider) { state.bankProvider = bankProvider; },
        [Mutations.SET_COMPLETE_URL](state, completeUrl) { state.completeUrl = completeUrl; },
        [Mutations.SET_CANCEL_URL](state, cancelUrl) { state.cancelUrl = cancelUrl; },
        [Mutations.SET_REQUESTID](state, requestId) { state.signup.requestId = requestId; },
        [Mutations.SET_INTERVALID](state, intervalId) { state.signup.intervalId = intervalId; },
        [Mutations.SET_FETCHING](state, fetching) { state.signup.fetching = fetching; },
        [Mutations.SET_PROGRESS](state, progress) { state.signup.progress = progress; },
        [Mutations.SET_CANCELED](state, canceled) { state.signup.canceled = canceled; },
        [Mutations.RESET_SIGNUP](state) {
            state.signup = {
                fetching: true,
                intervalId: -1,
                requestId: '',
                canceled: false,
                progress: {
                    code: OpenBankingProcessState.Created,
                },
            };
        },
    },
} as module;
